import { HttpParams } from '@angular/common/http';
import { Component, EventEmitter, Input, OnDestroy, Output, ViewChild } from '@angular/core';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { MatLegacyDialog as MatDialog, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { MatLegacySelect as MatSelect } from '@angular/material/legacy-select';
import { ApiLinks } from '@cutting/app.api-links';
import {
  ButtReplacementCustomDialogComponent,
  ButtReplacementCustomDialogData,
} from '@cutting/main/butt-replacement-custom-dialog/butt-replacement-custom-dialog.component';
import {
  BazisMaterialHelper,
  CuttingClientMaterial,
  CuttingClientOptions,
  CuttingMaterial,
  CuttingMaterialBaseDialogComponent,
  CuttingMaterialBaseDialogData,
  CuttingPanel,
  CuttingPanelHelper,
  CuttingPanelListModule,
  CuttingPanelListService,
  CuttingSettings,
  Guid,
  LegacyDialogsService,
  NgxLocalizedNumbersService,
  PanelListV2Component,
  PanelUtilsHelper,
  PresetSettings,
  S3UploadService,
} from 'cloud-shared-lib';
import * as _ from 'lodash';
import { Observable, Subject, concatMap, count, filter, finalize, map, of, takeUntil, tap } from 'rxjs';

import { AsyncPipe, NgFor, NgIf } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatLegacyButtonModule as MatButtonModule } from '@angular/material/legacy-button';
import { MatLegacyFormFieldModule as MatFormFieldModule } from '@angular/material/legacy-form-field';
import { MatLegacySelectModule as MatSelectModule } from '@angular/material/legacy-select';
import { MatLegacyTooltipModule as MatTooltipModule } from '@angular/material/legacy-tooltip';
import { ClientMaterialService } from '@cutting/main/shared/services/client-material.service';
import { UserSettingsService } from '@cutting/main/shared/services/user-settings.service';
import { OrderDetailService } from '../order-detail.service';
import { ButtReplacementBottomSheetComponent } from './butt-replacement-bottom-sheet/butt-replacement-bottom-sheet.component';

@Component({
  selector: 'cutting-materials',
  templateUrl: './materials.component.html',
  standalone: true,
  imports: [
    NgIf,
    NgFor,
    AsyncPipe,
    MatIconModule,
    MatButtonModule,
    MatFormFieldModule,
    MatSelectModule,
    MatTooltipModule,
    CuttingPanelListModule,
  ],
})
export class MaterialsComponent implements OnDestroy {
  @ViewChild('selectMaterial', { static: true }) selectMaterial: MatSelect;
  @ViewChild('panelList') panelList: PanelListV2Component;
  @Input() orderId: number;
  @Input() editableOrder = false;
  @Input() cuttingSettings: CuttingSettings;
  @Input() clientOptions: CuttingClientOptions;

  @Output() valid: EventEmitter<boolean> = new EventEmitter();

  presetSettings: PresetSettings;
  materialBaseDialogRef: MatDialogRef<CuttingMaterialBaseDialogComponent>;
  materials: CuttingMaterial[] = [];
  validPanels = true;
  buttReplacementCustomDialogRef: MatDialogRef<ButtReplacementCustomDialogComponent>;
  selectionMode = false;

  unsubscribe$: Subject<void> = new Subject();
  data$: Observable<CuttingMaterial[]>;
  selectedMaterial: CuttingMaterial | undefined;
  originalAttachments: string[] = [];

  get attachmentsToUpload(): File[] {
    return this.orderDetailService.panelAttachmentsToUpload;
  }
  set attachmentsToUpload(value: File[]) {
    this.orderDetailService.panelAttachmentsToUpload = value;
  }

  get isShowMaterialsPrice(): boolean {
    return this.cuttingSettings.estimateSettings.showMaterialsPrice;
  }

  get showQuantityInStock(): boolean {
    return this.cuttingSettings.showQuantityInStock;
  }

  constructor(
    public dialog: MatDialog,
    private clientMaterialService: ClientMaterialService,
    private orderDetailService: OrderDetailService,
    private bottomSheet: MatBottomSheet,
    private cuttingPanelListService: CuttingPanelListService,
    private localizedNumbersService: NgxLocalizedNumbersService,
    private s3UploadService: S3UploadService,
    private dialogsService: LegacyDialogsService,
    private userSettingsService: UserSettingsService
  ) {
    this.getMaterials();
    this.getPresetSettings();
  }

  getMaterialName(material: CuttingMaterial): string {
    return BazisMaterialHelper.formatMaterialNameAndArticle(material.name, material.article);
  }

  deleteSelectedMaterial(): void {
    const materialName = BazisMaterialHelper.formatMaterialNameAndArticle(
      this.selectedMaterial.name,
      this.selectedMaterial.article
    );
    const message = $localize`Удалить материал "${materialName}"?`;
    this.dialogsService
      .confirm(message)
      .pipe(
        filter(res => res),
        tap(() => {
          this.deleteMaterial(this.selectedMaterial);

          if (this.materials.length > 0) {
            this.selectedMaterial = this.materials[0];
          } else {
            this.selectedMaterial = undefined;
          }
        }),
        takeUntil(this.unsubscribe$)
      )
      .subscribe();
  }

  public hasEmptyPanels(): boolean {
    return this.materials.some(m => m.panels.some(CuttingPanelHelper.isEmptyPanel));
  }

  swapSelectedMaterialsFromMB(item: CuttingMaterial): void {
    if (!item || !this.selectedMaterial) {
      return;
    }

    const oldClientMaterial = this.clientMaterialService.materials$.value.find(
      m => m.inMaterialBaseId === this.selectedMaterial.inMaterialBaseId
    );

    this.clientMaterialService.replaceMaterial(
      'panel',
      {
        article: item.article,
        name: item.name,
        inMaterialBaseId: item.inMaterialBaseId,
        type: item.type,
        preparedForDeletion: false,
        cuttings: oldClientMaterial.cuttings ?? [],
        id: 0,
      } as CuttingClientMaterial,
      this.selectedMaterial.name,
      this.selectedMaterial.article
    );

    this.selectedMaterial.name = item.name;
    this.selectedMaterial.article = item.article;
    this.selectedMaterial.inMaterialBaseId = item.inMaterialBaseId;
    this.selectedMaterial.inMaterialBaseGroupId = item.inMaterialBaseGroupId;
    this.selectedMaterial.textureName = item.textureName;
    this.selectedMaterial.color = item.color;
    this.selectedMaterial.thickness = item.thickness;
    this.selectedMaterial.height = item.height;
    this.selectedMaterial.width = item.width;
  }

  addNewMaterial(item: CuttingMaterial): number {
    if (!item) {
      return;
    }

    const matIndex = this.materials.push(item) - 1;

    this.selectedMaterial = item;

    this.clientMaterialService.updateMaterials('panel', {
      article: item.article,
      name: item.name,
      inMaterialBaseId: item.inMaterialBaseId,
      type: item.type,
      preparedForDeletion: false,
      cuttings: [],
      id: 0,
    } as CuttingClientMaterial);

    return matIndex;
  }

  getMaterials(): void {
    /* TODO: Возможно, стоит сделать чтобы материалы запрашивались
       один раз и разбивались на фасады и нефасады за пределами этого компонента */
    this.data$ = this.orderDetailService.getMaterials().pipe(
      tap((response: CuttingMaterial[]) => {
        this.materials = response.filter(m => m.panels.length);
        this.originalAttachments = this.getAllAttachments(this.materials);

        this.selectedMaterial = this.materials[0];

        this.materials.forEach(m => {
          this.clientMaterialService.updateMaterials('panel', {
            article: m.article,
            name: m.name,
            inMaterialBaseId: m.inMaterialBaseId,
            type: m.type,
            preparedForDeletion: false,
            cuttings: [],
            id: 0,
          } as CuttingClientMaterial);
        });
      })
    );
  }

  getCurrencySymbol(): string {
    return this.localizedNumbersService.getCurrentLocaleDefinition().currency;
  }

  setPanelsPosInOrder(): void {
    let startIndex = 1;
    this.materials.forEach(m => {
      m.panels.forEach((item, index) => {
        item.position = (index + startIndex).toString();
      });

      startIndex += m.panels.length;
    });
  }

  setMissingPanelPositions(panelUUID: string | void = void 0): void {
    const getPosList = (): string[] =>
      this.materials
        .map(_m => _m.panels.map(_p => _p.position))
        .reduce((acum, val) => {
          acum.push(...val);
          return acum;
        });

    this.materials.forEach(m => {
      m.panels.forEach(item => {
        if (_.isEmpty(item.position.trim()) && (item.uuid === panelUUID || !panelUUID)) {
          item.position = this.generateUniquePosition(getPosList());
        }
      });
    });
  }

  openMaterialBaseDialog(): void {
    this.materialBaseDialogRef = this.dialog.open(CuttingMaterialBaseDialogComponent, {
      panelClass: 'material-base-material-list-dialog',
      disableClose: false,
      width: '1150px',
      data: {
        showPrices: this.isShowMaterialsPrice,
        currencyName: this.getCurrencySymbol(),
        materialsToExclude: [...this.materials],
        showQuantityInStock: this.showQuantityInStock,
        forPanel: true,
      } as CuttingMaterialBaseDialogData,
    });

    this.materialBaseDialogRef.afterClosed().subscribe((result: CuttingMaterial[] | undefined) => {
      if (result && result.length > 0) {
        result.forEach(m => {
          this.addNewMaterial(m);
        });
      }
      this.materialBaseDialogRef = null;
    });
  }

  openMaterialBaseDialogForSwap(): void {
    this.materialBaseDialogRef = this.dialog.open(CuttingMaterialBaseDialogComponent, {
      panelClass: 'material-base-material-list-dialog',
      disableClose: false,
      width: '1150px',
      data: {
        showPrices: this.isShowMaterialsPrice,
        currencyName: this.getCurrencySymbol(),
        materialsToExclude: [...this.materials],
        showQuantityInStock: this.showQuantityInStock,
      } as CuttingMaterialBaseDialogData,
    });

    const oldMaterial = _.cloneDeep(this.selectedMaterial);

    this.materialBaseDialogRef.afterClosed().subscribe((result: CuttingMaterial[] | undefined) => {
      if (result && result.length > 0) {
        this.swapSelectedMaterialsFromMB(result[0]);

        if (
          !this.cuttingPanelListService.onCuttingOptionsChanged.value.outlineEditorOptions.faceBanding
            .cadAddOtherMaterial
        ) {
          this.setMaterialForFaceBanding(this.selectedMaterial.panels, result[0]);
        }
        if (this.selectedMaterial.panels.length > 0 && this.materialIsUsedInFaceBanding(oldMaterial)) {
          PanelUtilsHelper.replaceFaceBanding(
            this.selectedMaterial.panels,
            {
              ...this.selectedMaterial,
              panels: [],
            },
            oldMaterial
          );
        }

        if (this.materialHasButts(this.selectedMaterial)) {
          this.openButtReplacementBottomSheet(
            this.selectedMaterial.panels,
            this.selectedMaterial.inMaterialBaseId,
            this.selectedMaterial.thickness
          ).then(newPanels => {
            this.selectedMaterial.panels = newPanels;
          });
        }
      }

      this.materialBaseDialogRef = null;
    });
  }

  swapButts(): void {
    this.buttReplacementCustomDialogRef = this.dialog.open(ButtReplacementCustomDialogComponent, {
      panelClass: 'butt-replacement-custom-dialog',
      disableClose: false,
      width: '500px',
      data: {
        panels: _.cloneDeep(this.selectedMaterial.panels),
        matInMatBaseId: this.selectedMaterial.inMaterialBaseId,
        materialThickness: this.selectedMaterial.thickness,
      } as ButtReplacementCustomDialogData,
    });

    this.buttReplacementCustomDialogRef.afterClosed().subscribe((result: CuttingPanel[]) => {
      if (result) {
        this.selectedMaterial.panels = result;
      }
      this.buttReplacementCustomDialogRef = null;
    });
  }

  openButtReplacementBottomSheet(
    panels: CuttingPanel[],
    newMaterialMatInMatBaseId: number,
    materialThickness: number
  ): Promise<CuttingPanel[]> {
    let bottomSheetRef = this.bottomSheet.open(ButtReplacementBottomSheetComponent, {
      ariaLabel: 'CuttingButt replacement',
      disableClose: true,
    });

    bottomSheetRef.instance.panels = panels;
    bottomSheetRef.instance.matInMatBaseId = newMaterialMatInMatBaseId;
    bottomSheetRef.instance.materialThickness = materialThickness;

    return new Promise(resolve => {
      bottomSheetRef.afterDismissed().subscribe((res: CuttingPanel[]) => {
        if (res) {
          resolve(res);
        }

        bottomSheetRef = null;
      });
    });
  }

  panelsListSelectionModeChange(selectionMode: boolean): void {
    this.selectionMode = selectionMode;
  }

  cloneSelectedPanels(panels: CuttingPanel[]): void {
    this.materialBaseDialogRef = this.dialog.open(CuttingMaterialBaseDialogComponent, {
      panelClass: 'material-base-material-list-dialog',
      disableClose: false,
      width: '1150px',
      data: {
        showPrices: this.isShowMaterialsPrice,
        currencyName: this.getCurrencySymbol(),
        materialsToExclude: [],
        multiple: false,
      } as CuttingMaterialBaseDialogData,
    });

    this.materialBaseDialogRef.afterClosed().subscribe((result: CuttingMaterial[] | undefined) => {
      if (result && result.length > 0) {
        let addedMaterialIndex = this.materials.findIndex(this.someMaterials(result[0]));

        if (addedMaterialIndex === -1) {
          addedMaterialIndex = this.addNewMaterial(result[0]);
        } else {
          this.selectedMaterial = this.materials[addedMaterialIndex];
        }

        const newPanels = _.cloneDeep(panels);

        newPanels.forEach(p => ((p.uuid = Guid.newGuid()), (p.position = '')));

        this.materials[addedMaterialIndex].panels = [
          ...this.materials[addedMaterialIndex].panels,
          ..._.cloneDeep(newPanels),
        ];
        this.setMissingPanelPositions();

        this.panelList.cancelPanelSelection();
      }
      this.materialBaseDialogRef = null;
    });
  }

  deleteEmptyPanels(): void {
    this.materials.forEach((item: CuttingMaterial) => {
      item.panels = item.panels.filter(p => !CuttingPanelHelper.isEmptyPanel(p));
    });
  }

  attachPanelFile(file: File): void {
    this.attachmentsToUpload.push(file);
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
  }

  private deleteMaterial(item: CuttingMaterial): void {
    const materialListIndex: number = this.materials.findIndex(this.someMaterials(item));
    if (materialListIndex !== -1) {
      this.materials.splice(materialListIndex, 1);
    }

    this.clientMaterialService.prepareForDeletion('panel', item.name, item.article);
  }

  private materialIsUsedInFaceBanding(oldMaterial: CuttingMaterial): boolean {
    return this.selectedMaterial.panels.some(item =>
      item.panelOutline.faceBanding.some(element => BazisMaterialHelper.compareMaterials(element.material, oldMaterial))
    );
  }

  private someMaterials(
    item: CuttingMaterial
  ): (value: CuttingMaterial, index: number, array: CuttingMaterial[]) => boolean {
    return (u): boolean =>
      u.name.toLowerCase().trim() === item.name.toLowerCase().trim() &&
      u.article.toLowerCase().trim() === item.article.toLowerCase().trim();
  }

  private getAllAttachments(materials: CuttingMaterial[]): string[] {
    return _(materials.flatMap(m => m.panels).map(p => p.attachmentName))
      .compact()
      .uniq()
      .value();
  }

  syncOrderAttachments(orderId: number): Observable<boolean> {
    const currentAttachments = this.getAllAttachments(this.materials);
    const deletedAttachments = _.difference(this.originalAttachments, currentAttachments);

    _.remove(this.attachmentsToUpload, a => !_.some(currentAttachments, c => c === a.name));

    const remover = deletedAttachments.length
      ? this.orderDetailService.removeAttachments(orderId, deletedAttachments)
      : of(null);

    return remover.pipe(
      concatMap(() =>
        this.s3UploadService.upload<File, { value: string }, void>(
          this.attachmentsToUpload,
          () => ApiLinks.api + 'order-attachments/upload-url',
          f => new HttpParams({ fromObject: { filename: f.name, orderId: orderId } }),
          r => r.value,
          () => ApiLinks.api + 'order-attachments/uploaded',
          f => new HttpParams({ fromObject: { filename: f.name, orderId: orderId, fileSize: f.size } })
        )
      ),
      count(),
      map(v => !!v),
      finalize(() => (this.attachmentsToUpload = []))
    );
  }

  /**************************************************************************/

  private generateUniquePosition(currentPositions: string[]): string {
    const nums: number[] = [];

    currentPositions.forEach(item => {
      if (item.trim() !== '' && _.isNumber(+item.trim())) {
        nums.push(+item);
      }
    });

    if (nums.length === 0) {
      return '1';
    }

    nums.sort((a, b) => (a < b ? -1 : a > b ? 1 : 0));

    if (nums.length > 1) {
      for (let i = 1; i < nums.length; i++) {
        if (nums[i] - nums[i - 1] > 1) {
          return (nums[i - 1] + 1).toString();
        }
      }
    } else {
      return (nums[0] + 1).toString();
    }

    return (nums[nums.length - 1] + 1).toString();
  }

  private setMaterialForFaceBanding(panels: CuttingPanel[], material: CuttingMaterial): void {
    panels.forEach(p => {
      if (p.panelOutline.faceBanding && p.panelOutline.faceBanding.length > 0) {
        p.panelOutline.faceBanding.forEach(f => {
          f.material = _.cloneDeep(material);
        });
      }
    });
  }

  private materialHasButts(material: CuttingMaterial): boolean {
    return material.panels.some(p => (p.l1 || p.l2 || p.w1 || p.w2 ? true : false));
  }

  private getPresetSettings(): void {
    this.userSettingsService
      .getPresetSettings()
      .pipe(
        takeUntil(this.unsubscribe$),
        concatMap(p => this.orderDetailService.getAvailableTariffOptions().pipe(map(t => [t.result, p] as const)))
      )
      .subscribe(([opt, ps]) => (this.presetSettings = this.disableNotPaidSettings(opt, ps)));
  }

  private disableNotPaidSettings(tariffOptions: string[], presetSettings: PresetSettings): PresetSettings {
    const availability = _.cloneDeep(presetSettings);

    if (_.isNil(tariffOptions)) {
      availability.componentsAvailability.outlineEditor.angles = false;
      availability.componentsAvailability.outlineEditor.fasteners = false;
      availability.componentsAvailability.outlineEditor.slots = false;

      return availability;
    }

    if (!tariffOptions.includes('ADDITIONAL_PROCESSING')) {
      availability.componentsAvailability.outlineEditor.angles = false;
      availability.componentsAvailability.outlineEditor.slots = false;
    }

    if (!tariffOptions.includes('HOLES')) {
      availability.componentsAvailability.outlineEditor.fasteners = false;
    }

    return availability;
  }
}
